import React, { useState, useEffect } from 'react';
import { useSchool } from '../context/SchoolContext';
import { AssignedClass } from '../types';
import styles from '../pages/TeacherDashboard.module.css';
import { formatClassLevelLabel } from '../utils/classLevelLabel';

interface ClassAnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    message: string;
    form: number;
    sendEmail: boolean;
  }) => Promise<void>;
  assignedClasses: AssignedClass[];
}

export const ClassAnnouncementModal: React.FC<ClassAnnouncementModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  assignedClasses,
}) => {
  const { currentSchool, getSchoolInfo } = useSchool();
  const schoolInfo = getSchoolInfo(currentSchool);
  
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [selectedForm, setSelectedForm] = useState<number | ''>('');
  const [sendEmail, setSendEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setTitle('');
      setMessage('');
      setSelectedForm('');
      setSendEmail(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }
    
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }
    
    if (selectedForm === '') {
      setError('Please select a class');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        title: title.trim(),
        message: message.trim(),
        form: selectedForm as number,
        sendEmail,
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create announcement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getClassDisplayName = (form: number): string => {
    return formatClassLevelLabel(currentSchool, form);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          maxWidth: 600,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: schoolInfo.theme.primaryColor,
            color: '#fff',
            padding: 24,
            borderRadius: '12px 12px 0 0',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                📢 Create Class Announcement
              </h2>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9, fontSize: 14 }}>
                Send an announcement to your students
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: '#fff',
                fontSize: 24,
                cursor: 'pointer',
                borderRadius: 6,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          {error && (
            <div
              style={{
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: 6,
                padding: 12,
                marginBottom: 20,
                color: '#c33',
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {/* Class Selection */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="class-select"
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 14,
                color: '#333',
              }}
            >
              Select Class <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <select
              id="class-select"
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value ? Number(e.target.value) : '')}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                border: '1px solid #d0d0d0',
                borderRadius: 6,
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = schoolInfo.theme.primaryColor;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d0d0d0';
              }}
            >
              <option value="">-- Select a class --</option>
              {assignedClasses
                .sort((a, b) => a.form - b.form)
                .map((cls) => (
                  <option key={cls.form} value={cls.form}>
                    {getClassDisplayName(cls.form)} - {cls.subjects.join(', ')}
                  </option>
                ))}
            </select>
          </div>

          {/* Title */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="announcement-title"
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 14,
                color: '#333',
              }}
            >
              Title <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <input
              id="announcement-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Important Class Update"
              maxLength={255}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                border: '1px solid #d0d0d0',
                borderRadius: 6,
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = schoolInfo.theme.primaryColor;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d0d0d0';
              }}
            />
          </div>

          {/* Message */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="announcement-message"
              style={{
                display: 'block',
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 14,
                color: '#333',
              }}
            >
              Message <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <textarea
              id="announcement-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your announcement message here..."
              rows={8}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 15,
                border: '1px solid #d0d0d0',
                borderRadius: 6,
                outline: 'none',
                transition: 'border-color 0.2s',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = schoolInfo.theme.primaryColor;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d0d0d0';
              }}
            />
          </div>

          {/* Email Option */}
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              background: '#f8f9fa',
              borderRadius: 6,
              border: '1px solid #e0e0e0',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                cursor: 'pointer',
                gap: 12,
              }}
            >
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                style={{
                  marginTop: 2,
                  width: 18,
                  height: 18,
                  cursor: 'pointer',
                }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 4 }}>
                  📧 Send as email to students
                </div>
                <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                  When enabled, this announcement will be sent to all students in the selected class
                  who have an announcement email configured.
                </div>
              </div>
            </label>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                fontSize: 15,
                fontWeight: 600,
                border: '1px solid #d0d0d0',
                borderRadius: 6,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                background: '#fff',
                color: '#666',
                transition: 'all 0.2s',
                opacity: isSubmitting ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.background = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.primaryButton}
              style={{
                padding: '12px 24px',
                fontSize: 15,
                fontWeight: 600,
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? '⏳ Creating...' : '📢 Create Announcement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

